import classNames from "classnames";
import Image from "next/image";
import { StrapiImage } from "~/shared-types";
import { getFormattedImageAttributes } from "~/utils/getImageAttributes";
import { Badge } from "../../Badge";
import { UseFormReturn } from "react-hook-form";
import { Inputs } from "../useUpsertProductForm";
import { Text } from "~/components/Text";

type Props = {
  form: UseFormReturn<Inputs>;
  className?: string;
};

export const FeaturedImageAspectPicker = ({ form, className }: Props) => {
  const featuredImageValue = form.watch("featuredImage");
  if (!featuredImageValue || !featuredImageValue[0]) return null;

  const featuredImageOrientation = form.watch("featuredImageOrientation");

  const onAspectImageClick = (aspectText: string) => {
    if (aspectText === "4:3") {
      form.setValue("featuredImageOrientation", "landscape", {
        shouldDirty: true,
      });
    }

    if (aspectText === "1:1") {
      form.setValue("featuredImageOrientation", "square", {
        shouldDirty: true,
      });
    }

    if (aspectText === "3:4") {
      form.setValue("featuredImageOrientation", "portrait", {
        shouldDirty: true,
      });
    }
  };

  const sharedProps = {
    featuredImage: featuredImageValue[0],
    onClick: onAspectImageClick,
  };

  return (
    <div className={className}>
      <Text>Vælg formatet for visning af dit billede:</Text>
      <div className="grid grid-cols-3 gap-2 items-end mt-4">
        <AspectImage
          {...sharedProps}
          aspectClass="aspect-[3/4]"
          aspectText="3:4"
          isActive={featuredImageOrientation === "portrait"}
        />

        <AspectImage
          {...sharedProps}
          aspectClass="aspect-square"
          aspectText="1:1"
          isActive={featuredImageOrientation === "square"}
        />

        <AspectImage
          {...sharedProps}
          aspectClass="aspect-[4/3]"
          aspectText="4:3"
          isActive={featuredImageOrientation === "landscape"}
        />
      </div>
    </div>
  );
};

type AspectImageProps = {
  aspectClass: string;
  aspectText: string;
  featuredImage: StrapiImage;
  onClick: (aspectText: string) => void;
  isActive?: boolean;
  className?: string;
};

const AspectImage = ({
  aspectClass,
  aspectText,
  featuredImage,
  onClick,
  isActive,
  className,
}: AspectImageProps) => {
  return (
    <button
      className={classNames(
        "relative rounded-xl hover:outline outline-secondary !cursor-pointer",
        className,
        isActive ? "outline outline-2 outline-success" : ""
      )}
      onClick={() => onClick(aspectText)}
    >
      <Image
        {...getFormattedImageAttributes(featuredImage, "large")}
        className={classNames(
          "object-cover rounded-xl pointer-events-none w-full",
          aspectClass
        )}
      />
      <Badge
        color={isActive ? "green" : "gray"}
        className="absolute top-2 left-2"
      >
        {aspectText}
      </Badge>
    </button>
  );
};
